import React, { useState, useEffect } from "react";
import {
  Languages,
  FileText,
  TrendingUp,
  Bot,
  ChevronRight,
  Sparkles,
  Shield,
  Clock,
  X,
  Building,
  Gpu,
  ChevronLeft,
  SlidersHorizontal,
  LayoutGrid,
  Target,
  Calendar,
  Circle,
  HardDrive,
  Banknote,
  Database,
  Edit,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import Logo from "./assets/logo.png";
import { useAuth } from "./hooks/useAuth";
import MainHeader from "./MainHeader";
import useDailyMission from "./hooks/useDailyMission";

const ITEMS_PER_SLIDE = 6;

const Home = () => {
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [isRepairModalOpen, setIsRepairModalOpen] = useState(false);
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [layoutMode, setLayoutMode] = useState("slide"); // "grid" or "slide"
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [activeQuest, setActiveQuest] = useState("today");
  const [expandAll, setExpandAll] = useState(true);
  const [showNotification, setShowNotification] = useState(true);
  const [dontShowToday, setDontShowToday] = useState(false);

  const { getDailyMission } = useDailyMission();

  // 데일리 미션 상태
  const [dailyStatus, setDailyStatus] = useState(true);
  const [daily, setDaily] = useState({
    today_tasks: [],
    tomorrow_tasks: [],
    week_high_priority: [],
    today_count: 0,
    tomorrow_count: 0,
    week_high_count: 0,
    unsolved_task_count: 0,
    footprints: 0,
  });

  /* ────────────────────────────────────────────────────────────────
     데이터 로드: /api/v1/dashboard/daily/mission
     status=false면 “가져오기 실패” 표기 + 모든 값 0 처리
     ──────────────────────────────────────────────────────────────── */
  useEffect(() => {
    let mounted = true;
    (async () => {
      const res = await getDailyMission();
      if (!mounted) return;
      setDaily(res.data);
      setDailyStatus(res.status);
    })();
    return () => {
      mounted = false;
    };
  }, []);

  /* ────────────────────────────────────────────────────────────────
     AI 자동화 서비스 데이터
     ──────────────────────────────────────────────────────────────── */
  const services = [
    // {
    //   key: "assistant",
    //   title: "Elva 비서",
    //   description: "24시간 상시 응답 가능한 인공지능 비서",
    //   icon: Sparkles,
    //   color: "blue",
    //   features: ["간단한 의사결정에 도움", "문맥 다듬기", "간단한 초안 작성"],
    //   path: "/assistant",
    //   gradient: "from-blue-500 to-cyan-600",
    // },
    {
      key: "translate",
      title: "문서 번역 시스템",
      description: "TGL 엔진을 활용한 고품질 문서 번역 서비스",
      icon: Languages,
      color: "blue",
      features: ["즉시 번역", "대량 문서 처리", "다국어 지원"],
      path: "/translate",
      gradient: "from-blue-500 to-cyan-600",
    },
    {
      key: "deep_research",
      title: "문서 심층 분석",
      description: "AI 기반의 다중 문서 심층 분석",
      icon: FileText,
      color: "purple",
      features: ["심층 분석", "문서 기반 채팅", "문서 간 비교 분석"],
      path: "/deep_research",
      badge: "Pro",
      gradient: "from-purple-500 to-pink-600",
    },
    {
      key: "insight",
      title: "AI 인사이트 분석",
      description: "실시간 웹 검색 기반 인사이트 제공",
      icon: TrendingUp,
      color: "orange",
      features: ["실시간 데이터", "트렌드 분석", "예측 모델링"],
      path: "/insight",
      gradient: "from-orange-500 to-red-600",
    },
    {
      key: "vcompany",
      title: "Virtual Biz Lab",
      description:
        "AI가 가상의 회사를 오픈하여 팀원들과 비즈니스 모델을 실험·검증을 진행합니다.",
      icon: Building,
      color: "green",
      features: ["넥스트 오피스", "비즈니스 실현", "아이디어 구체화"],
      path: "/vcompany",
      badge: "Preview",
      gradient: "from-teal-500 to-cyan-600",
    },
    {
      key: "agent",
      title: "Agent Elva",
      description: "다양한 AI 에이전트로 업무 자동화",
      icon: Bot,
      color: "indigo",
      features: ["맞춤형 에이전트", "업무 자동화", "멀티모달 지원"],
      path: "/agent_library",
      badge: "BETA",
      gradient: "from-indigo-500 to-purple-600",
    },
  ];

  // 기본 기능 데이터
  const basicFeatures = [
    {
      key: "assistant",
      title: "Elva 비서",
      description: "24시간 상시 응답 가능한 인공지능 비서",
      icon: Sparkles,
      color: "blue",
      features: [
        "업무·재무 등 다양한 내부시스템 통합",
        "AI기반 강화 검색엔진",
        "표준화된 AI서비스 지원",
      ],
      path: "/assistant",
      gradient: "from-blue-500 to-cyan-600",
    },
    {
      key: "schedule_management",
      title: "일정 관리",
      description:
        "개인 일정과 팀 일정을 한눈에 보고 간편하게 관리할 수 있어요.",
      icon: Clock,
      color: "purple",
      features: ["캘린더 통합", "할 일 목록 관리", "AI비서와 연동"],
      path: "/schedule_management",
    },
    {
      key: "edrive",
      title: "E-Drive",
      description:
        "회사 파일을 안전하게 보관하고 필요한 사람과 손쉽게 공유하세요.",
      icon: HardDrive,
      color: "blue",
      features: [
        "내 전용 드라이브",
        "팀 공유 드라이브",
        "동형 암호화를 통한 보안",
      ],
      path: "/edrive",
    },
    {
      key: "financial",
      title: "재정 관리",
      description:
        "팀 예산과 지출 내역을 쉽고 투명하게 기록·확인할 수 있습니다.",
      icon: Banknote,
      color: "green",
      features: ["팀별 스프레드시트", "손쉬운 지출 관리", "Excel 연동"],
      path: "/financial_management",
    },
    {
      key: "knowledge_database",
      title: "지식 데이터베이스",
      description:
        "SmartEditor를 위한 배경지식 자료로 변환하고 관리할 수 있습니다.",
      icon: Database,
      color: "blue",
      features: [
        "AI에게 배경지식 부여",
        "지식 데이터베이스",
        "통합 지식 관리 서비스",
      ],
      path: "/knowledge_database_section",
    },
    {
      key: "smart_editor",
      title: "스마트 에디터",
      description: "복잡한 문서를 AI와 함께 효율적으로 작성",
      icon: Edit,
      color: "pink",
      features: [
        "기초 아이디어 제공",
        "복잡한 문서 초안 작성",
        "지식 데이터베이스를 통한 AI에게 배경지식 부여",
      ],
      path: "/smart_editor_section",
    },
  ];

  /* ────────────────────────────────────────────────────────────────
     통계(Stats) 데이터
     ──────────────────────────────────────────────────────────────── */
  const randomSec = () => `${(Math.random() * (19 - 8) + 8).toFixed(1)}초`;
  const randomGPU = () => `${(Math.random() * (91 - 27) + 27).toFixed(1)}%`;
  const randomDocs = () => `${(Math.random() * (10 - 8) + 8).toFixed(0)}`;
  const stats = [
    { label: "GPU 사용률", value: randomGPU(), icon: Gpu },
    { label: "처리중인 문서", value: randomDocs(), icon: FileText },
    { label: "평균 응답 시간", value: randomSec(), icon: Clock },
    { label: "디스크 내구도", value: "99.781%", icon: Shield },
  ];

  /* ────────────────────────────────────────────────────────────────
     슬라이드 / 레이아웃 관련 로직
     ──────────────────────────────────────────────────────────────── */
  const nextSlide = () => {
    const totalSlides = Math.ceil(services.length / ITEMS_PER_SLIDE);
    setIsTransitioning(true);
    setTimeout(() => {
      setCurrentSlide((prev) => (prev + 1) % totalSlides);
      setIsTransitioning(false);
    }, 150);
  };

  const prevSlide = () => {
    const totalSlides = Math.ceil(services.length / ITEMS_PER_SLIDE);
    setIsTransitioning(true);
    setTimeout(() => {
      setCurrentSlide((prev) => (prev - 1 + totalSlides) % totalSlides);
      setIsTransitioning(false);
    }, 150);
  };

  const handleLayoutChange = (mode) => {
    setIsTransitioning(true);
    setTimeout(() => {
      setLayoutMode(mode);
      setIsTransitioning(false);
    }, 150);
  };

  const currentServices =
    layoutMode === "slide"
      ? services.slice(
          currentSlide * ITEMS_PER_SLIDE,
          (currentSlide + 1) * ITEMS_PER_SLIDE
        )
      : services;

  const totalSlides = Math.ceil(services.length / ITEMS_PER_SLIDE);

  /* ────────────────────────────────────────────────────────────────
     세션 / 알림 관련 로직
     ──────────────────────────────────────────────────────────────── */
  const handleLogout = () => logout();

  const handleClick = (service) => {
    if (service.badge === "Repair") {
      setIsRepairModalOpen(true);
    } else {
      navigate(service.path);
    }
  };

  const handleDontShowToday = () => {
    const today = new Date().toDateString();
    sessionStorage.setItem("hideQuestNotification", today);
    setDontShowToday(true);
    setShowNotification(false);
  };

  useEffect(() => {
    const today = new Date().toDateString();
    const hideDate = sessionStorage.getItem("hideQuestNotification");
    if (hideDate === today) {
      setShowNotification(false);
      setDontShowToday(true);
    }
  }, []);

  /* ────────────────────────────────────────────────────────────────
     데일리 미션 / 카운트 계산
     ──────────────────────────────────────────────────────────────── */
  const incompleteCounts = {
    unsolvedToday: daily.unsolved_task_count || 0,
    today: daily.today_count || 0,
    tomorrow: daily.tomorrow_count || 0,
    week: daily.week_high_count || 0,
  };

  const totalIncompleteTasks =
    incompleteCounts.unsolvedToday +
    incompleteCounts.today +
    incompleteCounts.tomorrow +
    incompleteCounts.week;

  /* ────────────────────────────────────────────────────────────────
     유틸: 미션 아이템 렌더링
     ──────────────────────────────────────────────────────────────── */
  const getTaskTitle = (task, idx) => {
    if (typeof task === "string") return task;
    if (!task || typeof task !== "object") return `할 일 ${idx + 1}`;
    return (
      task.title ||
      task.name ||
      task.summary ||
      task.content ||
      `할 일 ${task.id ?? idx + 1}`
    );
  };

  const getTaskTime = (task) => {
    if (typeof task !== "object" || !task) return null;
    return task.time || task.deadline || task.date || task.when || null;
  };

  const MissionItem = ({ task, index }) => {
    const title = getTaskTitle(task, index);
    const time = getTaskTime(task);

    return (
      <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
        <div className="flex items-center gap-3">
          <Circle className="w-4 h-4 text-gray-400" />
          <div className="flex flex-col">
            <span className="text-sm font-medium text-gray-700">{title}</span>
            {time && (
              <span className="text-xs text-gray-500 mt-0.5">{time}</span>
            )}
          </div>
        </div>
      </div>
    );
  };

  /* ────────────────────────────────────────────────────────────────
     리턴 (UI)
     ──────────────────────────────────────────────────────────────── */
  return (
    <>
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-purple-50">
        {/* 헤더 */}
        <MainHeader />

        {/* 히어로 섹션 */}
        <section className="relative overflow-hidden py-12">
          <div className="absolute inset-0">
            <div className="absolute top-10 left-10 w-52 h-52 bg-blue-400/10 rounded-full filter blur-3xl animate-pulse" />
            <div className="absolute bottom-10 right-10 w-64 h-64 bg-purple-400/10 rounded-full filter blur-3xl animate-pulse delay-700" />
          </div>

          <div className="relative max-w-7xl mx-auto px-6">
            <div className="bg-white/60 backdrop-blur-sm rounded-2xl p-8 shadow-lg border border-gray-100">
              <div className="flex flex-col md:flex-row items-center justify-between gap-6">
                <div className="flex-1">
                  <div className="inline-flex items-center gap-2 px-3 py-1 bg-blue-100 rounded-full text-blue-700 text-xs font-medium mb-3">
                    <Sparkles className="w-3 h-3" />
                    AI 업무 파트너
                  </div>
                  <h2 className="text-2xl md:text-3xl font-bold text-gray-800 mb-2">
                    Elva와 함께한 업무{" "}
                    <span className="text-blue-600">
                      {daily.footprints}일째
                    </span>
                  </h2>
                  <p className="text-gray-600 text-sm md:text-base">
                    매일 더 똑똑하고 효율적인 업무 환경을 만들어가고 있어요
                  </p>
                </div>

                <div className="flex flex-col items-center md:items-end gap-3">
                  <p className="text-sm text-gray-500 text-center md:text-right">
                    아직 Elva 사용이 익숙하지 않으신가요?
                  </p>
                  <button
                    onClick={() => navigate("/help_guide")}
                    className="px-6 py-2.5 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-full text-sm font-medium hover:shadow-lg transform hover:-translate-y-0.5 transition-all flex items-center gap-2"
                  >
                    시작하기 가이드
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* 서비스 섹션 */}
        <section id="services" className="py-15 px-6 pb-20">
          <div className="max-w-7xl mx-auto px-6">
            {/* 데일리 미션 섹션 */}
            <div className="mb-8">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-800">
                  일정을 한눈에 확인하세요!
                </h3>
                <button
                  onClick={() => {
                    if (expandAll) {
                      setActiveQuest(null);
                      setExpandAll(false);
                    } else {
                      setActiveQuest("all");
                      setExpandAll(true);
                    }
                  }}
                  className="text-sm text-gray-600 hover:text-gray-800 flex items-center gap-1"
                >
                  {expandAll ? "모두 접기" : "모두 펼치기"}
                  <ChevronRight
                    className={`w-4 h-4 transition-transform ${
                      expandAll ? "rotate-90" : ""
                    }`}
                  />
                </button>
              </div>

              {/* status=false면 실패 배너 표기 */}
              {!dailyStatus && (
                <div className="mb-4 p-3 text-sm rounded-lg border border-red-200 bg-red-50 text-red-700">
                  가져오기 실패
                </div>
              )}

              {/* 미션 카드 3열 */}
              <div className="grid md:grid-cols-3 gap-4">
                {/* 오늘 할 일 */}
                <div className="bg-white rounded-xl shadow-md overflow-hidden">
                  <div
                    className="flex items-center justify-between p-4 bg-gradient-to-r from-indigo-50 to-indigo-100 cursor-pointer hover:from-indigo-100 hover:to-indigo-200 transition-colors"
                    onClick={() => navigate("/schedule_management")}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center shadow-sm">
                        <Calendar className="w-5 h-5 text-indigo-600" />
                      </div>
                      <div>
                        <h4 className="font-medium text-gray-800">
                          오늘 할 일
                        </h4>
                        <p className="text-xs text-gray-600">
                          총 {daily.today_count}개
                        </p>
                      </div>
                    </div>
                  </div>
                  {(expandAll || activeQuest === "today") && (
                    <div className="p-4 space-y-2 max-h-64 overflow-y-auto">
                      {daily.today_tasks.length > 0 ? (
                        daily.today_tasks.map((task, idx) => (
                          <MissionItem key={idx} task={task} index={idx} />
                        ))
                      ) : (
                        <div className="p-3 text-sm text-gray-500 bg-gray-50 rounded-lg">
                          오늘 등록된 할 일이 없습니다.
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* 내일 일정 */}
                <div className="bg-white rounded-xl shadow-md overflow-hidden">
                  <div
                    className="flex items-center justify-between p-4 bg-gradient-to-r from-blue-50 to-blue-100 cursor-pointer hover:from-blue-100 hover:to-blue-200 transition-colors"
                    onClick={() => navigate("/schedule_management")}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center shadow-sm">
                        <Calendar className="w-5 h-5 text-blue-600" />
                      </div>
                      <div>
                        <h4 className="font-medium text-gray-800">내일 일정</h4>
                        <p className="text-xs text-gray-600">
                          총 {daily.tomorrow_count}개
                        </p>
                      </div>
                    </div>
                  </div>
                  {(expandAll || activeQuest === "tomorrow") && (
                    <div className="p-4 space-y-2 max-h-64 overflow-y-auto">
                      {daily.tomorrow_tasks.length > 0 ? (
                        daily.tomorrow_tasks.map((task, idx) => (
                          <MissionItem key={idx} task={task} index={idx} />
                        ))
                      ) : (
                        <div className="p-3 text-sm text-gray-500 bg-gray-50 rounded-lg">
                          내일 일정이 없습니다.
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* 이번주 주요일정 */}
                <div className="bg-white rounded-xl shadow-md overflow-hidden">
                  <div
                    className="flex items-center justify-between p-4 bg-gradient-to-r from-purple-50 to-purple-100 cursor-pointer hover:from-purple-100 hover:to-purple-200 transition-colors"
                    onClick={() => navigate("/schedule_management")}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center shadow-sm">
                        <Target className="w-5 h-5 text-purple-600" />
                      </div>
                      <div>
                        <h4 className="font-medium text-gray-800">
                          이번주 주요일정
                        </h4>
                        <p className="text-xs text-gray-600">
                          총 {daily.week_high_count}개
                        </p>
                      </div>
                    </div>
                  </div>
                  {(expandAll || activeQuest === "week") && (
                    <div className="p-4 space-y-2 max-h-64 overflow-y-auto">
                      {daily.week_high_priority.length > 0 ? (
                        daily.week_high_priority.map((task, idx) => (
                          <MissionItem key={idx} task={task} index={idx} />
                        ))
                      ) : (
                        <div className="p-3 text-sm text-gray-500 bg-gray-50 rounded-lg">
                          이번주 주요일정이 없습니다.
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* 플랜 라벨 */}
            <div className="mb-6">
              <span className="text-sm font-semibold text-indigo-600 uppercase tracking-wider border-b-2 border-indigo-600 pb-1">
                현재 사용자 플랜: MAX
              </span>
            </div>

            {/* 기본 기능 섹션 */}
            <div className="mb-8">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">
                기본 서비스
              </h3>
              <div className="grid md:grid-cols-3 gap-4">
                {basicFeatures.map((feature) => (
                  <div
                    key={feature.key}
                    onClick={() => navigate(feature.path)}
                    className="bg-white rounded-xl p-6 shadow-md hover:shadow-lg transition-all cursor-pointer group"
                  >
                    <div className="flex items-center mb-3">
                      <div
                        className={`w-10 h-10 ${
                          feature.color === "blue"
                            ? "bg-blue-100 group-hover:bg-blue-200"
                            : feature.color === "green"
                            ? "bg-green-100 group-hover:bg-green-200"
                            : "bg-purple-100 group-hover:bg-purple-200"
                        } rounded-lg flex items-center justify-center mr-3 transition-colors`}
                      >
                        <feature.icon
                          className={`w-5 h-5 ${
                            feature.color === "blue"
                              ? "text-blue-600"
                              : feature.color === "green"
                              ? "text-green-600"
                              : "text-purple-600"
                          }`}
                        />
                      </div>
                      <h4 className="font-medium text-gray-800">
                        {feature.title}
                      </h4>
                    </div>
                    <p className="text-sm text-gray-600 mb-3">
                      {feature.description}
                    </p>
                    <ul className="text-xs text-gray-500 space-y-1">
                      {feature.features.map((f, i) => (
                        <li key={i}>• {f}</li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>

            {/* AI 자동화 섹션 */}
            <h3 className="text-lg font-semibold text-gray-800 mb-4">
              AI 자동화 서비스
            </h3>

            {/* 레이아웃 전환 버튼 */}
            <div className="flex gap-1 p-1 rounded-lg pb-5">
              <button
                onClick={() => handleLayoutChange("slide")}
                className={`p-2 rounded-md transition-all duration-200 ${
                  layoutMode === "slide"
                    ? "bg-white text-indigo-600 shadow-sm"
                    : "text-gray-500 hover:text-gray-700"
                }`}
                title="Slide View"
              >
                <SlidersHorizontal className="w-5 h-5" />
              </button>
              <button
                onClick={() => handleLayoutChange("grid")}
                className={`p-2 rounded-md transition-all duration-200 ${
                  layoutMode === "grid"
                    ? "bg-white text-indigo-600 shadow-sm"
                    : "text-gray-500 hover:text-gray-700"
                }`}
                title="Grid View"
              >
                <LayoutGrid className="w-5 h-5" />
              </button>
            </div>

            {/* 컨테이너 */}
            <div
              className={`transition-all duration-300 ${
                isTransitioning ? "opacity-0 scale-95" : "opacity-100 scale-100"
              }`}
            >
              {layoutMode === "slide" ? (
                <div className="relative px-16">
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {currentServices.map((service) => (
                      <div
                        key={service.key}
                        onClick={() => handleClick(service)}
                        className="group relative bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 cursor-pointer overflow-hidden transform hover:-translate-y-1"
                      >
                        <div
                          className={`absolute inset-0 bg-gradient-to-br ${service.gradient} opacity-0 group-hover:opacity-5 transition-opacity`}
                        />
                        {service.badge && (
                          <div
                            className={`absolute top-4 right-4 px-3 py-1 text-xs font-bold rounded-full ${
                              service.badge === "Pro"
                                ? "bg-purple-100 text-purple-700"
                                : service.badge === "Preview"
                                ? "bg-blue-100 text-blue-700"
                                : "bg-amber-100 text-amber-700"
                            }`}
                          >
                            {service.badge}
                          </div>
                        )}

                        <div className="p-8">
                          <div
                            className={`w-16 h-16 bg-gradient-to-br ${service.gradient} rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform`}
                          >
                            <service.icon className="w-8 h-8 text-white" />
                          </div>

                          <h3 className="text-xl font-semibold text-gray-800 mb-2">
                            {service.title}
                          </h3>
                          <p className="text-gray-600 mb-6">
                            {service.description}
                          </p>

                          <ul className="space-y-2 mb-6">
                            {service.features.map((feature, index) => (
                              <li
                                key={index}
                                className="flex items-center gap-2 text-sm text-gray-500"
                              >
                                <div className="w-1.5 h-1.5 rounded-full bg-indigo-500" />
                                {feature}
                              </li>
                            ))}
                          </ul>

                          <div className="flex items-center text-sm font-medium text-gray-700 group-hover:text-blue-600 transition-colors">
                            시작하기
                            <ChevronRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                          </div>
                        </div>
                      </div>
                    ))}

                    {/* 빈 공간 채우기 */}
                    {currentServices.length < 4 &&
                      Array.from({ length: 4 - currentServices.length }).map(
                        (_, index) => (
                          <div key={`empty-${index}`} className="invisible">
                            <div className="bg-transparent rounded-2xl p-8">
                              <div className="w-16 h-16 mb-6" />
                              <div className="h-7 mb-2" />
                              <div className="h-20 mb-6" />
                              <div className="h-24" />
                            </div>
                          </div>
                        )
                      )}
                  </div>

                  {/* 슬라이드 네비게이션 */}
                  {totalSlides > 1 && (
                    <>
                      <button
                        onClick={prevSlide}
                        className="absolute left-0 top-1/2 -translate-y-1/2 bg-white rounded-full p-3 shadow-lg hover:shadow-xl transition-all duration-200 hover:scale-110"
                      >
                        <ChevronLeft className="w-6 h-6 text-gray-600" />
                      </button>
                      <button
                        onClick={nextSlide}
                        className="absolute right-0 top-1/2 -translate-y-1/2 bg-white rounded-full p-3 shadow-lg hover:shadow-xl transition-all duration-200 hover:scale-110"
                      >
                        <ChevronRight className="w-6 h-6 text-gray-600" />
                      </button>
                      <div
                        className="flex justify-center gap-2 mt-8"
                        style={{ gridAutoRows: "minmax(0, 1fr)" }}
                      >
                        {Array.from({ length: totalSlides }).map((_, index) => (
                          <button
                            key={index}
                            onClick={() => setCurrentSlide(index)}
                            className={`h-2 rounded-full transition-all duration-300 ${
                              currentSlide === index
                                ? "w-8 bg-indigo-600"
                                : "w-2 bg-gray-300 hover:bg-gray-400"
                            }`}
                          />
                        ))}
                      </div>
                    </>
                  )}
                </div>
              ) : (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {services.map((service) => (
                    <div
                      key={service.key}
                      onClick={() => handleClick(service)}
                      className="group relative bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 cursor-pointer overflow-hidden transform hover:-translate-y-1"
                    >
                      <div
                        className={`absolute inset-0 bg-gradient-to-br ${service.gradient} opacity-0 group-hover:opacity-5 transition-opacity`}
                      />
                      {service.badge && (
                        <div
                          className={`absolute top-4 right-4 px-3 py-1 text-xs font-bold rounded-full ${
                            service.badge === "Pro"
                              ? "bg-purple-100 text-purple-700"
                              : service.badge === "Preview"
                              ? "bg-blue-100 text-blue-700"
                              : "bg-amber-100 text-amber-700"
                          }`}
                        >
                          {service.badge}
                        </div>
                      )}

                      <div className="p-8">
                        <div
                          className={`w-16 h-16 bg-gradient-to-br ${service.gradient} rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform`}
                        >
                          <service.icon className="w-8 h-8 text-white" />
                        </div>

                        <h3 className="text-xl font-semibold text-gray-800 mb-2">
                          {service.title}
                        </h3>
                        <p className="text-gray-600 mb-6">
                          {service.description}
                        </p>

                        <ul className="space-y-2 mb-6">
                          {service.features.map((feature, index) => (
                            <li
                              key={index}
                              className="flex items-center gap-2 text-sm text-gray-500"
                            >
                              <div className="w-1.5 h-1.5 rounded-full bg-indigo-500" />
                              {feature}
                            </li>
                          ))}
                        </ul>

                        <div className="flex items-center text-sm font-medium text-gray-700 group-hover:text-blue-600 transition-colors">
                          시작하기
                          <ChevronRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </section>

        {/* 통계 섹션 */}
        <section id="stats" className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-800 mb-4">
                플랫폼 현황
              </h2>
              <p className="text-gray-600">
                실시간으로 업데이트되는 서비스 지표
              </p>
            </div>
            <div className="grid md:grid-cols-4 gap-8">
              {stats.map((stat, index) => (
                <div key={index} className="text-center">
                  <div className="w-16 h-16 bg-gradient-to-br from-blue-100 to-purple-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <stat.icon className="w-8 h-8 text-blue-600" />
                  </div>
                  <div className="text-3xl font-bold text-gray-800 mb-2">
                    {stat.value}
                  </div>
                  <div className="text-gray-600">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>
      </div>

      {/* NPC 알림 말풍선 */}
      {showNotification && totalIncompleteTasks > 0 && !dontShowToday && (
        <div className="fixed bottom-6 right-6 z-50">
          <div className="relative">
            <div className="bg-white rounded-2xl shadow-2xl p-4 max-w-xs border-2 border-gray-100">
              <button
                onClick={() => setShowNotification(false)}
                className="absolute -top-2 -right-2 w-6 h-6 bg-gray-200 rounded-full flex items-center justify-center hover:bg-gray-300 transition-colors"
              >
                <X className="w-3 h-3 text-gray-600" />
              </button>
              <div className="flex items-start gap-3">
                <div className="w-12 h-12 bg-white from-indigo-500 to-purple-600 rounded-full flex items-center justify-center flex-shrink-0">
                  <img
                    src={Logo}
                    alt="elva bot "
                    className="w-12 h-12 text-white"
                  />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-800 mb-2">
                    안녕하세요! 오늘 확인할 업무가 있어요 📋
                  </p>
                  <div className="space-y-1 text-xs text-gray-600">
                    {incompleteCounts.unsolvedToday > 0 && (
                      <p>
                        • 오늘 미처리 업무: {incompleteCounts.unsolvedToday}개
                      </p>
                    )}
                    {incompleteCounts.today > 0 && (
                      <p>• 오늘 할 일: {incompleteCounts.today}개</p>
                    )}
                    {incompleteCounts.tomorrow > 0 && (
                      <p>• 내일 일정: {incompleteCounts.tomorrow}개</p>
                    )}
                    {incompleteCounts.week > 0 && (
                      <p>• 이번주 주요일정: {incompleteCounts.week}개</p>
                    )}
                  </div>
                  <button
                    onClick={handleDontShowToday}
                    className="mt-3 text-xs text-indigo-600 hover:text-indigo-800 underline"
                  >
                    오늘 하루 보지 않기
                  </button>
                </div>
              </div>
              <div className="absolute -bottom-2 right-8 w-4 h-4 bg-white transform rotate-45 border-r-2 border-b-2 border-gray-100" />
            </div>
          </div>
        </div>
      )}

      {/* 점검 모달 */}
      {isRepairModalOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={() => setIsRepairModalOpen(false)}
        >
          <div
            className="bg-white rounded-2xl p-6 max-w-md mx-6 text-center shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-xl font-semibold mb-4">서비스 점검 중입니다</h2>
            <p className="text-gray-700 mb-6">
              현재 성능 및 서비스 품질 문제로 수정 중에 있습니다.
              <br />
              빠른 시일 내에 다시 오픈하겠습니다.
            </p>
            <button
              onClick={() => setIsRepairModalOpen(false)}
              className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700 transition"
            >
              확인
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default Home;
